import { useQuery } from '@tanstack/react-query'
import { Award, BookOpen, CalendarDays, ClipboardPen, Layers, Pencil, Ruler, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { ApiError, errorMessage } from '@/api/errors'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { StatCard } from '@/components/ui/StatCard'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { Can } from '@/features/auth/guards'
import { examQuery, useDeleteExam } from '@/features/exams/api'
import { ExamStatusBadge } from '@/features/exams/components/ExamBadges'
import { ExamDialog } from '@/features/exams/components/ExamDialog'
import { MarkEntryPanel } from '@/features/exams/components/MarkEntryPanel'
import { PapersPanel } from '@/features/exams/components/PapersPanel'
import { ProgressMeter } from '@/features/exams/components/ProgressMeter'
import { ResultsPanel } from '@/features/exams/components/ResultsPanel'
import { TransitionActions } from '@/features/exams/components/TransitionActions'
import { progressPercent } from '@/features/exams/status'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate, formatNumber, formatPercent } from '@/utils/format'

const TABS = ['papers', 'marks', 'results'] as const
type Tab = (typeof TABS)[number]

export function ExamDetailPage() {
  const { t } = useTranslation('exams')
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const exam = useQuery(examQuery(id))
  const remove = useDeleteExam()
  const [dialog, setDialog] = useState<'edit' | 'delete' | null>(null)
  useDocumentTitle(exam.data?.name ?? 'Exam')

  if (exam.isPending) return <LoadingState />
  if (exam.error) {
    return exam.error instanceof ApiError && exam.error.status === 404 ? (
      <NotFoundState resource="exam" homeHref="/admin/exams" />
    ) : (
      <ErrorState onRetry={() => void exam.refetch()} />
    )
  }
  const x = exam.data
  const requested = params.get('tab')
  const tab: Tab = TABS.includes(requested as Tab)
    ? (requested as Tab)
    : x.status === 'published' || x.status === 'locked'
      ? 'results'
      : x.status === 'marks_open'
        ? 'marks'
        : 'papers'
  const close = (open: boolean) => (open ? undefined : setDialog(null))
  const editable = !['published', 'locked'].includes(x.status)
  const deletable = ['draft', 'scheduled'].includes(x.status)

  return (
    <>
      <PageHeader
        title={x.name}
        breadcrumbs={[{ label: t('Exams'), to: '/admin/exams' }, { label: x.name }]}
        homeHref="/admin"
        description={`${x.examType.name} · ${x.academicYear.name} · ${t('graded with {{scale}}', { scale: x.gradingScale.name })}`}
        meta={<ExamStatusBadge status={x.status} />}
        actions={
          <Can permission="exams.manage">
            {editable ? (
              <Button variant="secondary" leftIcon={<Pencil />} onClick={() => setDialog('edit')}>
                {t('Edit')}
              </Button>
            ) : null}
            {deletable ? (
              <Button variant="danger-soft" leftIcon={<Trash2 />} onClick={() => setDialog('delete')}>
                {t('Delete')}
              </Button>
            ) : null}
            <TransitionActions exam={x} />
          </Can>
        }
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('Dates')}
          value={<span className="text-lg">{formatDate(x.startsOn)}</span>}
          hint={t('until {{date}}', { date: formatDate(x.endsOn) })}
          icon={<CalendarDays />}
        />
        <StatCard
          label={t('Classes')}
          value={x.classes.length}
          hint={x.classes.map((c) => c.name).join(', ') || '—'}
          icon={<Layers />}
          tone="violet"
        />
        <StatCard
          label={t('Papers')}
          value={x.paperCount}
          hint={
            x.weightPercent !== null
              ? t('Weight {{weight}}% of the final result', { weight: x.weightPercent })
              : t('No weighting set')
          }
          icon={<BookOpen />}
          tone="info"
        />
        <StatCard
          label={t('Marks entered')}
          value={formatPercent(progressPercent(x.marksEntered, x.marksExpected))}
          icon={<ClipboardPen />}
          tone={x.marksExpected > 0 && x.marksEntered >= x.marksExpected ? 'success' : 'warning'}
          hint={t('{{entered}} of {{expected}}', {
            entered: formatNumber(x.marksEntered),
            expected: formatNumber(x.marksExpected),
          })}
          footer={
            <ProgressMeter
              className="mt-3"
              compact
              entered={x.marksEntered}
              expected={x.marksExpected}
              label={t('Marks entered')}
            />
          }
        />
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList aria-label={t('Exam sections')}>
          <TabsTrigger value="papers" icon={<Ruler />} count={x.paperCount}>
            {t('Papers')}
          </TabsTrigger>
          <TabsTrigger value="marks" icon={<ClipboardPen />}>
            {t('Mark entry')}
          </TabsTrigger>
          <TabsTrigger value="results" icon={<Award />}>
            {t('Results')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="papers">
          <PapersPanel exam={x} />
        </TabsContent>
        <TabsContent value="marks">
          <MarkEntryPanel exam={x} />
        </TabsContent>
        <TabsContent value="results">
          <ResultsPanel
            exam={x}
            reportCardHref={(studentId) => `/admin/exams/${x.id}/students/${studentId}`}
          />
        </TabsContent>
      </Tabs>

      {dialog === 'edit' ? <ExamDialog open onOpenChange={close} exam={x} /> : null}
      {dialog === 'delete' ? (
        <ConfirmDialog
          open
          onOpenChange={close}
          title={t('Delete {{name}}?', { name: x.name })}
          description={t('The exam and its papers are removed. This cannot be undone.')}
          confirmLabel={t('Delete exam')}
          loading={remove.isPending}
          onConfirm={() =>
            remove.mutate(x.id, {
              onSuccess: () => {
                toast.success(t('Exam deleted'), x.name)
                void navigate('/admin/exams')
              },
              onError: (e) => toast.error(t('Could not delete the exam'), errorMessage(e)),
            })
          }
        />
      ) : null}
    </>
  )
}
